import useCurrentMember from '@/member/hooks/useCurrentMember'
import { useOrgContext } from '@/org/contexts/OrgContext'
import { Crisp } from 'crisp-sdk-web'
import { useEffect } from 'react'
import {
  AVATAR_SM_WIDTH,
  getResizedImageUrl,
} from '@rolebase/shared/helpers/getResizedImageUrl'
import { useAuth } from '@/user/hooks/useAuth'

// Crisp valide ses entrées de façon synchrone et throw (ex. « Invalid avatar »,
// « Invalid email ») si la valeur ne lui convient pas. Comme ces appels sont
// déclenchés dans des effets de rendu, une exception non rattrapée fait planter
// l'arbre React → page blanche. On isole donc chaque appel au widget tiers.
function safeCrisp(fn: () => void) {
  try {
    fn()
  } catch (error) {
    console.warn('[Crisp] appel ignoré suite à une erreur du widget :', error)
  }
}

// Crisp n'accepte qu'une URL https absolue comme avatar.
const isValidHttpsUrl = (value: unknown): value is string =>
  typeof value === 'string' && /^https:\/\//i.test(value.trim())

export default function CrispSetUser() {
  const { user } = useAuth()
  const member = useCurrentMember()
  const { org } = useOrgContext()

  const email = user?.email
  const nickname = member?.name || user?.displayName
  const avatar =
    getResizedImageUrl(member?.picture, AVATAR_SM_WIDTH) || user?.avatarUrl

  useEffect(() => {
    if (!email) return
    safeCrisp(() => Crisp.user.setEmail(email))
  }, [email])

  useEffect(() => {
    if (!nickname) return
    safeCrisp(() => Crisp.user.setNickname(nickname))
  }, [nickname])

  useEffect(() => {
    // N'appeler setAvatar que si l'avatar est une URL https absolue valide.
    // Sinon on saute : Crisp throw « Invalid avatar » sur une valeur invalide.
    if (!isValidHttpsUrl(avatar)) return
    safeCrisp(() => Crisp.user.setAvatar(avatar))
  }, [avatar])

  useEffect(() => {
    if (!org) return
    safeCrisp(() => Crisp.user.setCompany(org.name, {}))
  }, [org?.name])

  return null
}
